import { Icon, Popup } from "semantic-ui-react";
import { Link } from "react-router-dom";

import { Checkbox } from "@/components/ui/checkbox";
import startCase from "lodash/startCase";

import TagList from "components/TagList";
import { DEFAULT_PAGE_SIZE, SELECT_COLUMN_ID } from "constants/Constants";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import { formatDate } from "components/helpers";
import { CASE_TIERS } from "constants/Case";
import MachineStatusModal from "routes/machine/modals/MachineStatusModal";
import MachineService from "services/Machine";
import MachineApprovalStatusService from "services/MachineApprovalStatus";
import CaseService from "services/Case";
import ProductLineService from "services/ProductLine";
import { NEGATABLE_OPERATORS } from "components/NewAdvancedSearch/filters/selectOperators";

const renderCountCell = ({ getValue }) => {
  const value = getValue();
  if (!value || Object.keys(value).length === 0) {
    return null;
  }
  return (
    <ScrollColumn>
      {Object.entries(value)
        .sort((a, b) => (a[1] > b[1] ? -1 : 1))
        .map(entry => (
          <li key={entry[0]}>{`${entry[0]}: ${entry[1]}`}</li>
        ))}
    </ScrollColumn>
  );
};

const renderCoordCell =
  (locationType, openMapModal) =>
  ({ getValue, row }) => {
    const value = getValue();
    if (!value) {
      return null;
    }
    return (
      <ScrollColumn>
        {Object.entries(value)
          .sort((a, b) => (a[1] > b[1] ? -1 : 1))
          .map(entry => {
            const hasLocations = entry[0] !== "No Data";
            const key = `${row.original.machine_id}-${entry[0]}`;
            const textVal = `${entry[0]}: ${entry[1]}`;
            if (hasLocations) {
              const [lat, lng] = entry[0].split(",");
              const center = { lat, lng };
              return (
                <li key={key}>
                  <a
                    onClick={() =>
                      openMapModal({
                        modelType: "Machine",
                        modelId: row.original.id,
                        locationType: locationType || "wifi",
                        name: `Machine ${row.original.machine_id}`,
                        center,
                      })
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {textVal}
                  </a>
                </li>
              );
            }
            return <li key={key}>{textVal}</li>;
          })}
      </ScrollColumn>
    );
  };

export const getMachinesTableColumns = ({
  caseRouteId,
  openMapModal,
  invalidateData,
  enableAegisAutomationStatus = false,
}) => {
  return [
    {
      id: SELECT_COLUMN_ID,
      size: 50,
      enableSorting: false,
      meta: {
        isDisplay: true,
      },
      header: ({ table }) => (
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onCheckedChange: checked => table.toggleAllRowsSelected(!!checked),
            "aria-label": "Select all rows",
          }}
        />
      ),
      cell: ({ row }) => (
        <div>
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected() && !row.getIsSelected(),
              onCheckedChange: checked => row.toggleSelected(!!checked),
              "aria-label": "Select row",
            }}
          />
        </div>
      ),
    },
    {
      header: "Machine ID",
      id: "Machine ID",
      accessorKey: "machine_id",
      size: 150,
      cell: ({ row }) => (
        <Link
          to={`/cases/${caseRouteId ?? row.original?.case_internal_id}/events/?machine_id=${encodeURIComponent(
            row.original.machine_id
          )}&page=1&per_page=${DEFAULT_PAGE_SIZE}`}
        >
          {row.original.machine_id}
        </Link>
      ),
    },
    // Every row on a case-scoped table shares one case, so the case identity is
    // only worth a column where the rows can span several cases.
    ...(caseRouteId
      ? []
      : [
          {
            header: "Case #",
            id: "Case #",
            accessorKey: "case_id",
            size: 80,
            cell: ({ row }) => {
              const value = row.original.case_id;
              if (!value) return "N/A";
              return (
                <TableLink href={`/cases/${row.original.case_internal_id}`}>
                  {value}
                </TableLink>
              );
            },
          },
          {
            header: "Case ID",
            id: "Case ID",
            accessorKey: "case_internal_id",
            size: 80,
            cell: ({ row }) => row.original.case_internal_id || "N/A",
          },
        ]),
    {
      header: "Product Line",
      id: "Product Line",
      accessorKey: "product_line_name",
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.original.tags}
          modelType="Machine"
          modelId={row.original.id}
          tableCell
          onUpdate={invalidateData}
        />
      ),
    },
    {
      header: "Case Tier",
      id: "Case Tier",
      accessorKey: "case_tier",
      cell: ({ row }) => {
        const value = row.original.case_tier;
        return value === CASE_TIERS.UNUSABLE ? "Unusable" : value;
      },
    },
    {
      header: "Approval Status",
      id: "Approval Status",
      accessorKey: "machine_approval_status_id",
      cell: ({ row }) => {
        return (
          <MachineStatusModal
            machineStatusId={row.original.machine_approval_status_id}
            machineStatusName={row.original.machine_approval_status}
            machineIds={[row.original.id]}
            fetchFn={invalidateData}
          />
        );
      },
    },
    {
      header: "Automation Status",
      id: "Automation Status",
      accessorKey: "automation_status",
    },
    // Keyed on the foreign key because the backend sorts machines by column,
    // not by relationship; the cell only maps that id to its label and
    // renders no control, which is what keeps the column read-only.
    ...(enableAegisAutomationStatus
      ? [
          {
            header: "Aegis Automation Status",
            id: "Aegis Automation Status",
            accessorKey: "aegis_automation_status_id",
            cell: ({ row }) => row.original.aegis_automation_status ?? null,
          },
        ]
      : []),
    {
      header: "Products",
      id: "Products",
      accessorKey: "products",
      enableSorting: false,
      cell: ({ row }) => {
        const products = row.original.products;
        if (!products?.length) return null;
        return (
          <ScrollColumn>
            {products.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "First Event",
      id: "First Event",
      accessorKey: "first_event",
      size: 232,
      cell: ({ row }) => {
        const value = row.original.first_event;
        return value ? formatDate(value) : null;
      },
    },
    {
      header: "Last Event",
      id: "Last Event",
      accessorKey: "last_event",
      size: 232,
      cell: ({ row }) => {
        const value = row.original.last_event;
        return value ? formatDate(value) : null;
      },
    },
    {
      header: "Total # Events",
      id: "Total # Events",
      accessorKey: "num_events",
    },
    {
      header: "Recent # Events",
      id: "Recent # Events",
      accessorKey: "num_recent_events",
    },
    {
      header: "Quality Score",
      id: "Quality Score",
      accessorKey: "quality_score",
    },
    {
      header: "Event Types",
      id: "Event Types",
      accessorKey: "event_types",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Email Domains",
      id: "Email Domains",
      accessorKey: "email_domains",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Computer Domains",
      id: "Computer Domains",
      accessorKey: "computer_domains",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Active MAC",
      id: "Active MAC",
      accessorKey: "active_mac",
    },
    {
      header: "Gateway MAC Addresses",
      id: "Gateway MAC Addresses",
      accessorKey: "gateway_mac_addresses",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "IP Country",
      id: "IP Country",
      accessorKey: "ip_country",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Machine Locale",
      id: "Machine Locale",
      accessorKey: "machine_locale",
      enableSorting: false,
      cell: ({ row }) => {
        const machineLocale = row.original.machine_locale;
        if (!machineLocale?.length) return null;
        return (
          <ScrollColumn>
            {machineLocale.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "Nearby Events",
      id: "Nearby Events",
      accessorKey: "proximity",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original.proximity;
        if (!value) return null;
        const { color, limit } = value;
        const { id, machine_id, distance } = row.original;
        let content;
        if (limit) {
          content = (
            <div>
              Case events detected within {limit} meters.{" "}
              <strong>({distance} meters)</strong>
              <br />
              <a
                onClick={() =>
                  openMapModal({
                    modelType: "Machine",
                    modelId: id,
                    name: `Machine ${machine_id}`,
                  })
                }
                style={{ cursor: "pointer" }}
              >
                Show on map
              </a>
            </div>
          );
        } else {
          content = (
            <>
              No nearby case events detected
              {!!distance && <strong>({distance} meters)</strong>}
            </>
          );
        }
        return (
          <Popup
            hoverable
            content={content}
            position={"top center"}
            trigger={
              <Icon
                key={`proximity`}
                size="large"
                color={color}
                name="circle"
              />
            }
          />
        );
      },
    },
    {
      header: "WiFi Locations",
      id: "WiFi Locations",
      accessorKey: "wifi_locations",
      enableSorting: false,
      cell: renderCoordCell("wifi", openMapModal),
    },
    {
      header: "IP Locations",
      id: "IP Locations",
      accessorKey: "ip_locations",
      enableSorting: false,
      cell: renderCoordCell("ip", openMapModal),
    },
    {
      header: "Hostname",
      id: "Hostname",
      accessorKey: "hostname",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.original.hostname || []).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Username",
      id: "Username",
      accessorKey: "username",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.original.username || []).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Active WiFi Access Point",
      id: "Active WiFi Access Point",
      accessorKey: "active_wifi_access_point",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.original.active_wifi_access_point || []).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Additional Email Addresses",
      id: "Additional Email Addresses",
      accessorKey: "additional_email_addresses",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.original.additional_email_addresses || []).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Client Email Addresses",
      id: "Client Email Addresses",
      accessorKey: "client_email_addresses",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.original.client_email_addresses || []).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Public IP Organization",
      id: "Public IP Organization",
      accessorKey: "public_ip_organization",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Version",
      id: "Version",
      accessorKey: "version",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "DNS Domain",
      id: "DNS Domain",
      accessorKey: "dns_domain",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Licensed To",
      id: "Licensed To",
      accessorKey: "licensed_to",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "License Info",
      id: "License Info",
      accessorKey: "license_info",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Additional License Info",
      id: "Additional License Info",
      accessorKey: "additional_license_info",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Public IP State",
      id: "Public IP State",
      accessorKey: "public_ip_state",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Public IP City",
      id: "Public IP City",
      accessorKey: "public_ip_city",
      enableSorting: false,
      cell: renderCountCell,
    },
    {
      header: "Public IP Address",
      id: "Public IP Address",
      accessorKey: "public_ip_address",
      enableSorting: false,
      cell: renderCountCell,
    },
  ];
};

const validateMacAddress = mac => {
  if (!mac || typeof mac !== "string") return false;

  const macPatterns = [
    /^([0-9A-Fa-f]{1,2}[:-]){5}([0-9A-Fa-f]{1,2})$/, // XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX
    /^[0-9A-Fa-f]{12}$/, // XXXXXXXXXXXX
  ];

  return macPatterns.some(pattern => pattern.test(mac.trim()));
};

export const getMachinesTableFilters = ({
  tags,
  caseId,
  entityId,
  productLineId,
  isStandalone,
  enableAegisAutomationStatus = false,
}) => {
  const nullNotNullOptions = [
    {
      id: "null",
      name: "Is null",
    },
    {
      id: "not_null",
      name: "Is not null",
    },
  ];

  // The repository helpers backing the MAC address, IP country and event type
  // options return nothing without a case or entity, so an unscoped view can
  // only offer a null/not-null choice on those keys.
  const hasCaseScope = !!(caseId || entityId);

  return [
    {
      key: "first_event",
      title: "First Event Date",
      type: "dateRange",
    },
    {
      key: "last_event",
      title: "Last Event Date",
      type: "dateRange",
    },
    // Both option lists are unscoped, so they can only be offered where there is
    // no narrower scope whose cases they would leak past.
    ...(isStandalone
      ? [
          {
            key: "case_number",
            title: "Case #",
            type: "ruvixxSelect",
            queryFn: filters => CaseService.getCaseNumbersForFilters(filters),
          },
          {
            key: "case_internal_id",
            title: "Case ID",
            type: "ruvixxSelect",
            queryFn: filters => CaseService.getForFilters(filters),
          },
        ]
      : []),
    ...(entityId
      ? [
          {
            key: "case_id",
            title: "Case",
            type: "ruvixxSelect",
            queryFn: filters =>
              CaseService.getForFilters({
                entity_id: entityId,
                use_external_case_id: true,
                ...filters,
              }),
            // No None option: a machine's case is NOT NULL, so "no case" can
            // never match a row. The backend also reads this key as a list of
            // external case ids, which has no null form to send.
          },
        ]
      : []),
    // Machines on a case-scoped table all share one product line, so only
    // offer the filter where they can span several (e.g. the entity page).
    ...(productLineId
      ? []
      : [
          {
            key: "product_lines",
            title: "Product Line",
            type: "ruvixxSelect",
            operators: NEGATABLE_OPERATORS,
            queryFn: ProductLineService.getForFilters,
          },
        ]),
    {
      key: "case_tier",
      title: "Case Tier",
      type: "select",
      operators: NEGATABLE_OPERATORS,
      data: Object.keys(CASE_TIERS).map(key => {
        return {
          id: CASE_TIERS[key],
          name: startCase(key.toLowerCase()),
        };
      }),
    },
    {
      key: "machine_approval_status_id",
      title: "Machine Approval Status",
      type: "ruvixxSelect",
      queryFn: filters =>
        MachineApprovalStatusService.getForFilters({
          ...filters,
        }),
    },
    {
      key: "machine_id",
      title: "Machine ID",
      type: "ruvixxSelect",
      queryFn: filters =>
        MachineService.getForFilters({
          case_id: caseId,
          entity_id: entityId,
          product_line_id: productLineId,
          ...filters,
        }),
    },
    {
      key: "num_events",
      title: "Total # Events",
      type: "range",
    },
    {
      key: "num_recent_events",
      title: "Recent # Events",
      type: "range",
    },
    {
      key: "quality_score",
      title: "Quality Score",
      type: "range",
    },
    {
      key: "email_domains",
      title: "Email Domains",
      type: "select",
      data: nullNotNullOptions,
      isSingle: true,
      clearable: true,
    },
    {
      key: "computer_domains",
      title: "Computer Domains",
      type: "select",
      data: nullNotNullOptions,
      isSingle: true,
      clearable: true,
    },
    {
      key: "wifi_locations",
      title: "WiFi Locations",
      type: "select",
      data: nullNotNullOptions,
      isSingle: true,
      clearable: true,
    },
    {
      key: "ip_locations",
      title: "IP Locations",
      type: "select",
      data: nullNotNullOptions,
      isSingle: true,
      clearable: true,
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    {
      key: "automation_status",
      title: "Automation Status",
      type: "select",
      operators: NEGATABLE_OPERATORS,
      data: [
        { id: "approvable", name: "Approvable" },
        { id: "rejected", name: "Rejected" },
        { id: "unset", name: "Unset" },
      ],
    },
    ...(enableAegisAutomationStatus
      ? [
          {
            key: "aegis_automation_status_id",
            title: "Aegis Automation Status",
            type: "ruvixxSelect",
            queryFn: filters =>
              MachineApprovalStatusService.getForFilters({
                ...filters,
              }),
          },
        ]
      : []),
    {
      key: "products",
      title: "Products",
      type: "select",
      data: nullNotNullOptions,
      isSingle: true,
      clearable: true,
    },
    {
      key: "gateway_mac_addresses",
      title: "Gateway MAC Address",
      ...(hasCaseScope
        ? {
            type: "listFilter",
            stateKey: "macFilters",
            validateInput: validateMacAddress,
            allowAdditions: false,
            buttonText: "Add gateway MAC",
            placeholder: "(XX:XX:XX:XX:XX:XX)...",
            dataFn: () =>
              MachineService.getGatewayMacAddresses({
                case_id: caseId,
                entity_id: entityId,
                product_line_id: productLineId,
              }),
          }
        : {
            type: "select",
            data: nullNotNullOptions,
            isSingle: true,
            clearable: true,
          }),
    },
    {
      key: "ip_country",
      title: "IP Country",
      ...(hasCaseScope
        ? {
            type: "listFilter",
            stateKey: "ipCountryFilters",
            allowAdditions: false,
            buttonText: "Add IP country",
            placeholder: "Type to search countries...",
            dataFn: () =>
              MachineService.getIPCountryCodes({
                case_id: caseId,
                entity_id: entityId,
                product_line_id: productLineId,
              }),
          }
        : {
            type: "select",
            data: nullNotNullOptions,
            isSingle: true,
            clearable: true,
          }),
    },
    ...(hasCaseScope
      ? [
          {
            key: "event_type",
            title: "Event Type",
            type: "ruvixxSelect",
            queryFn: filters =>
              MachineService.getEventTypes({
                case_id: caseId,
                entity_id: entityId,
                product_line_id: productLineId,
                ...filters,
              }),
          },
        ]
      : []),
  ];
};
